import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { DndContext, DragOverlay, closestCorners } from "@dnd-kit/core";
import { APPLICANT_TYPES, SORT } from "constants/index";
import { useApplicantBoardDnd, useApplicants } from "hooks";
import { colors } from "style";
import ApplicantNotSelectedModal from "./ApplicantNotSelectedModal";
import ApplicantBoardCard from "./ApplicantBoardCard";
import ApplicantBoardColumn from "./ApplicantBoardColumn";
import ApplicationDetailsModalForClient from "./ApplicationDetailsModalForClient";

const BOARD_COLUMNS = [
  {
    id: APPLICANT_TYPES.pending,
    title: "Missions.Applicants.Board.Column.Applied",
  },
  {
    id: APPLICANT_TYPES.applied,
    title: "Missions.Applicants.Board.Column.InReview",
  },
  {
    id: APPLICANT_TYPES.shortlisted,
    title: "Missions.Applicants.Board.Column.Shortlisted",
  },
  {
    id: APPLICANT_TYPES.interviewed,
    title: "Missions.Applicants.Board.Column.Interviewing",
  },
  {
    id: APPLICANT_TYPES.selected,
    title: "Missions.Applicants.Board.Column.Selected",
  },
  {
    id: APPLICANT_TYPES.unselected,
    title: "Missions.Applicants.Board.Column.Unselected",
  },
];

/**
 * ApplicantBoard
 *
 * @param {Object}      defaultSelected             The default selected mission and role IDs
 * @param {Object}      selectedProp                The selected mission and role IDs
 * @param {Object}      resolvedSort                The resolved sort options for the applicants
 * @param {Object}      variablesProp               Additional variables for the GraphQL queries
 * @param {Component}   applicationModalComponent   The component to render for the application modal
 * @param {Function}    modalConfigFunc             Function to configure the modal based on the application
 *
 * Customer-friendly ATS board that lets clients move applicants between existing applicant states.
 */
const ApplicantBoard = ({
  defaultSelected,
  selected: selectedProp,
  resolvedSort,
  variablesProp,
  applicationModalComponent: ApplicationModalComponent,
  modalConfigFunc,
}) => {
  const [columns, setColumns] = useState(getInitialColumns());
  const [application, setApplication] = useState(null);
  const source = selectedProp || defaultSelected || {};

  const queryArgs = {
    missionId: source?.missionId,
    missionRoleIds: source?.missionRoleIds,
    organizationId: source?.organizationId,
    keyword: source?.keyword,
    isMember: source?.isMember,
    advancedFilters: source?.advancedFilters,
    resultsPerPage: 100,
    sortKey: resolvedSort?.sortKey || source?.sortKey || "date",
    sortDirection: resolvedSort?.sortDirection || source?.sortDirection || SORT.desc,
    variables: variablesProp,
  };

  const pending = useApplicants({ ...queryArgs, applicantState: APPLICANT_TYPES.pending });
  const applied = useApplicants({ ...queryArgs, applicantState: APPLICANT_TYPES.applied });
  const shortlisted = useApplicants({ ...queryArgs, applicantState: APPLICANT_TYPES.shortlisted });
  const interviewed = useApplicants({ ...queryArgs, applicantState: APPLICANT_TYPES.interviewed });
  const selected = useApplicants({ ...queryArgs, applicantState: APPLICANT_TYPES.selected });
  const unselected = useApplicants({ ...queryArgs, applicantState: APPLICANT_TYPES.unselected });

  const queryResultsByState = {
    [APPLICANT_TYPES.pending]: pending,
    [APPLICANT_TYPES.applied]: applied,
    [APPLICANT_TYPES.shortlisted]: shortlisted,
    [APPLICANT_TYPES.interviewed]: interviewed,
    [APPLICANT_TYPES.selected]: selected,
    [APPLICANT_TYPES.unselected]: unselected,
  };

  useEffect(() => {
    setColumns(
      BOARD_COLUMNS.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: queryResultsByState[column.id]?.data || [],
        }),
        {}
      )
    );
  }, [pending.data, applied.data, shortlisted.data, interviewed.data, selected.data, unselected.data]);

  const {
    sensors,
    activeApplicant,
    notSelectedApplicant,
    setNotSelectedApplicant,
    handleDragStart,
    handleDragCancel,
    handleDragEnd,
    handleNotSelectedToastMessages,
  } = useApplicantBoardDnd({ columns, setColumns });

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={(event) => handleDragEnd({ ...event, validColumnIds: BOARD_COLUMNS.map((column) => column.id) })}
      >
        <div css={styles.board}>
          {BOARD_COLUMNS.map((column) => (
            <ApplicantBoardColumn
              key={column.id}
              column={column}
              applicants={columns[column.id] || []}
              loading={queryResultsByState[column.id]?.loading}
              onView={setApplication}
            />
          ))}
        </div>
        <DragOverlay>
          {activeApplicant && <ApplicantBoardCard applicant={activeApplicant} onView={setApplication} isOverlay />}
        </DragOverlay>
      </DndContext>

      {application && (
        <ApplicationModalComponent
          application={application}
          show={!!application}
          setApplication={setApplication}
          {...modalConfigFunc?.(application)}
        />
      )}

      {notSelectedApplicant && (
        <ApplicantNotSelectedModal
          show={!!notSelectedApplicant}
          applicantIds={[notSelectedApplicant.id]}
          onClose={() => setNotSelectedApplicant(null)}
          handleToastMessages={(...args) =>
            handleNotSelectedToastMessages(
              queryResultsByState[APPLICANT_TYPES.unselected]?.handleToastMessages,
              ...args
            )
          }
        />
      )}
    </>
  );
};

const getInitialColumns = () =>
  BOARD_COLUMNS.reduce(
    (acc, column) => ({
      ...acc,
      [column.id]: [],
    }),
    {}
  );

const styles = {
  board: css`
    display: flex;
    gap: 1rem;
    overflow-x: auto;
    padding: 0.25rem 0 1rem;
    scrollbar-color: ${colors.grayAnatomyLight2} transparent;
  `,
};

ApplicantBoard.propTypes = {
  defaultSelected: PropTypes.object,
  selected: PropTypes.object,
  resolvedSort: PropTypes.object,
  variablesProp: PropTypes.object,
  applicationModalComponent: PropTypes.elementType,
  modalConfigFunc: PropTypes.func,
};

ApplicantBoard.defaultProps = {
  applicationModalComponent: ApplicationDetailsModalForClient,
};

export default ApplicantBoard;
